<template>
  <div class="uk-container register-user-page">
    <UcariHeader />
    <div class="register-user-shell">
      <form class="register-user-card" @submit.prevent="register" novalidate>
        <div class="register-user-heading">
          <h2>Create Your UCARI Account</h2>
          <p>
            Results will not appear until all three steps are completed. After your kit is registered,
            results are typically available a few business days after we receive your hair sample.
          </p>
        </div>

        <div v-if="message" class="form-alert" :class="{ success: registrationCompleted }">
          {{ message }}
        </div>

        <div class="uk-grid-small" uk-grid>
          <div class="uk-width-1-1">
            <label class="field-label" for="register-email">Email <span class="field-meta">Required</span></label>
            <input
              id="register-email"
              v-model.trim="form.email"
              class="uk-input"
              :class="inputClass('email')"
              type="email"
              autocomplete="email"
              inputmode="email"
              @blur="handleEmailBlur"
              @input="handleInput('email')"
            />
            <div v-if="showError('email')" class="error">{{ fieldError('email') }}</div>
          </div>

          <div class="uk-width-1-2@s">
            <label class="field-label" for="register-first-name">First Name <span class="field-meta">Required</span></label>
            <input
              id="register-first-name"
              v-model.trim="form.firstName"
              class="uk-input"
              :class="inputClass('firstName')"
              type="text"
              autocomplete="given-name"
              @blur="markTouched('firstName')"
              @input="handleInput('firstName')"
            />
            <div v-if="showError('firstName')" class="error">{{ fieldError('firstName') }}</div>
          </div>

          <div class="uk-width-1-2@s">
            <label class="field-label" for="register-last-name">Last Name <span class="field-meta">Required</span></label>
            <input
              id="register-last-name"
              v-model.trim="form.lastName"
              class="uk-input"
              :class="inputClass('lastName')"
              type="text"
              autocomplete="family-name"
              @blur="markTouched('lastName')"
              @input="handleInput('lastName')"
            />
            <div v-if="showError('lastName')" class="error">{{ fieldError('lastName') }}</div>
          </div>

          <div class="uk-width-1-2@s">
            <label class="field-label" for="register-mobile">Mobile Number <span class="field-meta">Optional</span></label>
            <input
              id="register-mobile"
              v-model="form.mobileNumber"
              class="uk-input"
              :class="inputClass('mobileNumber')"
              type="tel"
              autocomplete="tel"
              inputmode="tel"
              placeholder="(555) 123-4567"
              @blur="markTouched('mobileNumber')"
              @input="handleMobileInput"
            />
            <div v-if="showError('mobileNumber')" class="error">{{ fieldError('mobileNumber') }}</div>
          </div>

          <div class="uk-width-1-2@s">
            <label class="field-label" for="register-zip">ZIP or Postal Code <span class="field-meta">Optional</span></label>
            <input
              id="register-zip"
              v-model.trim="form.zipCode"
              class="uk-input"
              :class="inputClass('zipCode')"
              type="text"
              autocomplete="postal-code"
              inputmode="text"
              @blur="markTouched('zipCode')"
              @input="handleInput('zipCode')"
            />
            <div v-if="showError('zipCode')" class="error">{{ fieldError('zipCode') }}</div>
          </div>

          <div class="uk-width-1-1">
            <label class="field-label" for="register-referral">How did you hear about us? <span class="field-meta">Optional</span></label>
            <select
              id="register-referral"
              v-model="form.referralSource"
              class="uk-select"
              :class="inputClass('referralSourceOther')"
              @change="handleReferralChange"
              @blur="markTouched('referralSource')"
            >
              <option value="">Select an option</option>
              <option v-for="option in referralOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
          </div>

          <div v-if="form.referralSource === 'other'" class="uk-width-1-1">
            <label class="field-label" for="register-referral-other">Tell us more <span class="field-meta">Required when Other is selected</span></label>
            <input
              id="register-referral-other"
              v-model.trim="form.referralSourceOther"
              class="uk-input"
              :class="inputClass('referralSourceOther')"
              type="text"
              maxlength="120"
              @blur="markTouched('referralSourceOther')"
              @input="handleInput('referralSourceOther')"
            />
            <div v-if="showError('referralSourceOther')" class="error">{{ fieldError('referralSourceOther') }}</div>
          </div>

          <div class="uk-width-1-2@s">
            <label class="field-label" for="register-password">Password <span class="field-meta">Required</span></label>
            <div class="uk-inline full-width">
              <input
                id="register-password"
                :type="showPassword ? 'text' : 'password'"
                v-model="form.password"
                class="uk-input"
                :class="inputClass('password')"
                autocomplete="new-password"
                @blur="markTouched('password')"
                @input="handlePasswordInput"
              />
              <a class="uk-form-icon uk-form-icon-flip" @click.prevent="togglePasswordVisibility">
                {{ showPassword ? 'Hide' : 'Show' }}
              </a>
            </div>
            <div v-if="showError('password')" class="error">{{ fieldError('password') }}</div>
          </div>

          <div class="uk-width-1-2@s">
            <label class="field-label" for="register-confirm-password">Confirm Password <span class="field-meta">Required</span></label>
            <div class="uk-inline full-width">
              <input
                id="register-confirm-password"
                :type="showConfirmPassword ? 'text' : 'password'"
                v-model="form.confirmPassword"
                class="uk-input"
                :class="inputClass('confirmPassword')"
                autocomplete="new-password"
                @blur="markTouched('confirmPassword')"
                @input="handlePasswordInput"
              />
              <a class="uk-form-icon uk-form-icon-flip" @click.prevent="toggleConfirmPasswordVisibility">
                {{ showConfirmPassword ? 'Hide' : 'Show' }}
              </a>
            </div>
            <div v-if="showError('confirmPassword')" class="error">{{ fieldError('confirmPassword') }}</div>
          </div>

        </div>

        <div class="password-requirements">
          <div><strong>Password must contain:</strong></div>
          <ul>
            <li :class="{ valid: passwordHasLowercase }">One lowercase letter</li>
            <li :class="{ valid: passwordHasUppercase }">One uppercase letter</li>
            <li :class="{ valid: passwordHasNumber }">One number</li>
            <li :class="{ valid: passwordHasSpecialChar }">One special character: {{ passwordAllowedSpecialCharacters }}</li>
            <li :class="{ valid: passwordHasLength }">At least 8 characters</li>
          </ul>
          <div class="uk-text-small">{{ passwordAllowedCharactersText }}</div>
        </div>

        <div class="email-opt-in">
          <label>
            <input
              v-model="form.receiveEducationalEmails"
              class="uk-checkbox uk-margin-small-right"
              type="checkbox"
            />
            Receive educational emails and special offers?
          </label>
        </div>

        <div class="terms-copy uk-margin-bottom">
          By creating an account, you agree to our
          <a href="https://ucari.com/termsofservice" target="_blank" rel="noopener noreferrer">Terms of Service</a>
          and
          <a href="https://ucari.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
        </div>

        <button class="uk-button uk-button-primary uk-width-1-1" type="submit" :disabled="submitDisabled">
          <span v-if="loading" uk-spinner="ratio: 0.6" class="uk-margin-small-right"></span>
          {{ loading ? 'Creating account...' : (registrationCompleted ? 'Account created' : 'Create account') }}
        </button>

        <div class="register-user-footer-copy">
          Already a customer? <router-link to="/login">Sign In</router-link>
        </div>
      </form>
    </div>
    <UcariFooter />
  </div>
</template>

<script>
import axios from 'axios';
import UcariHeader from './UcariHeader.vue';
import UcariFooter from './UcariFooter.vue';
import {
  PASSWORD_ALLOWED_CHARACTERS_TEXT,
  PASSWORD_ALLOWED_SPECIAL_CHARACTERS,
  validatePassword,
  toggleVisibility
} from '@/utils/common';
import {
  buildRegistrationPayload,
  formatMobileNumberInput,
  referralOptions,
  validateRegistrationForm
} from '@/utils/registrationForms';

export default {
  components: {
    UcariHeader,
    UcariFooter
  },
  data() {
    return {
      form: {
        email: '',
        firstName: '',
        lastName: '',
        mobileNumber: '',
        zipCode: '',
        referralSource: '',
        referralSourceOther: '',
        password: '',
        confirmPassword: '',
        receiveEducationalEmails: false
      },
      touched: {},
      message: '',
      loading: false,
      registrationCompleted: false,
      showPassword: false,
      showConfirmPassword: false,
      passwordMismatch: '',
      isPasswordInvalid: false,
      emailExistsError: '',
      emailCheckPending: false,
      formSubmitted: false,
      referralOptions
    };
  },
  created() {
    document.title = 'Create Account | UCARI';
  },
  computed: {
    passwordAllowedCharactersText() {
      return PASSWORD_ALLOWED_CHARACTERS_TEXT;
    },
    passwordAllowedSpecialCharacters() {
      return PASSWORD_ALLOWED_SPECIAL_CHARACTERS;
    },
    validationErrors() {
      const errors = validateRegistrationForm(this.form);
      if (this.isPasswordInvalid && this.form.password) {
        errors.password = 'Use a stronger password.';
      }
      if (this.passwordMismatch) {
        errors.confirmPassword = this.passwordMismatch;
      }
      if (this.emailExistsError) {
        errors.email = this.emailExistsError;
      }
      return errors;
    },
    submitDisabled() {
      return this.loading || this.registrationCompleted || this.emailCheckPending || Object.keys(this.validationErrors).length > 0;
    },
    passwordHasLowercase() {
      return /[a-z]/.test(this.form.password);
    },
    passwordHasUppercase() {
      return /[A-Z]/.test(this.form.password);
    },
    passwordHasNumber() {
      return /\d/.test(this.form.password);
    },
    passwordHasSpecialChar() {
      return /[!@#$%^&*()\-+{}|:;<>?/\\]/.test(this.form.password);
    },
    passwordHasLength() {
      return this.form.password.length >= 8;
    }
  },
  methods: {
    fieldError(field) {
      return this.validationErrors[field] || '';
    },
    inputClass(field) {
      return { 'error-border': this.showError(field) };
    },
    showError(field) {
      return (this.touched[field] || this.formSubmitted) && !!this.fieldError(field);
    },
    markTouched(field) {
      this.touched = { ...this.touched, [field]: true };
    },
    handleInput(field) {
      this.markTouched(field);
      if (field === 'email') {
        this.emailExistsError = '';
      }
      if (field === 'referralSourceOther' && this.message) {
        this.message = '';
      }
    },
    handleMobileInput(event) {
      this.form.mobileNumber = formatMobileNumberInput(event.target.value);
      this.markTouched('mobileNumber');
    },
    handleReferralChange() {
      this.markTouched('referralSource');
      if (this.form.referralSource !== 'other') {
        this.form.referralSourceOther = '';
      }
    },
    handlePasswordInput() {
      this.markTouched('password');
      this.markTouched('confirmPassword');
      const { isPasswordInvalid, passwordMismatch } = validatePassword(this.form.password, this.form.confirmPassword);
      this.isPasswordInvalid = isPasswordInvalid;
      this.passwordMismatch = passwordMismatch;
    },
    togglePasswordVisibility() {
      this.showPassword = toggleVisibility(this.showPassword);
    },
    toggleConfirmPasswordVisibility() {
      this.showConfirmPassword = toggleVisibility(this.showConfirmPassword);
    },
    async handleEmailBlur() {
      this.markTouched('email');
      this.emailExistsError = '';
      const email = String(this.form.email || '').trim();
      if (!email || this.validationErrors.email === 'Enter a valid email address.') {
        return;
      }

      this.emailCheckPending = true;
      try {
        const response = await axios.post(`${process.env.VUE_APP_API_URL}/api/check-email`, { email });
        if (response.data.exists) {
          this.emailExistsError = 'Email already registered.';
        }
      } catch (error) {
        this.emailExistsError = error.response?.data?.error || 'Unable to validate email right now.';
      } finally {
        this.emailCheckPending = false;
      }
    },
    async register() {
      this.formSubmitted = true;
      this.handlePasswordInput();
      await this.handleEmailBlur();

      if (this.submitDisabled) {
        this.message = 'Please correct the highlighted fields.';
        return;
      }

      this.loading = true;
      this.message = '';

      try {
        const payload = buildRegistrationPayload(this.form);
        const response = await axios.post(`${process.env.VUE_APP_API_URL}/api/register`, payload);
        if (response.status === 200 && response.data.message === 'Registration successful. Please check your email for verifying your email.') {
          this.registrationCompleted = true;
          this.message = response.data.message;
          this.$router.push('/registration-successful');
        }
      } catch (error) {
        this.message = error.response?.data?.error || 'Unable to create your account right now.';
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.register-user-page {
  padding-bottom: 48px;
}

.register-user-shell {
  display: flex;
  justify-content: center;
  padding: 24px 0 40px;
}

.register-user-card {
  width: 100%;
  max-width: 760px;
}

.register-user-heading h2 {
  margin-bottom: 8px;
}

.register-user-heading p {
  margin-bottom: 24px;
  color: #536051;
}

.field-label {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.field-meta {
  font-size: 12px;
  font-weight: 500;
  color: #71806e;
}

.full-width {
  width: 100%;
}

.password-requirements {
  margin: 18px 0;
  padding: 16px;
  border-radius: 16px;
  background: #eef4ea;
}

.password-requirements ul {
  margin: 10px 0 0;
  padding: 0;
  list-style: none;
}

.password-requirements li {
  position: relative;
  padding-left: 24px;
  margin-bottom: 6px;
  color: #6d746c;
}

.password-requirements li::before {
  content: '\2713';
  position: absolute;
  left: 0;
  color: transparent;
}

.password-requirements li.valid {
  color: #2f6a34;
}

.password-requirements li.valid::before {
  color: #2f6a34;
}

.email-opt-in {
  margin-top: 18px;
  color: #536051;
}

.terms-copy,
.register-user-footer-copy {
  margin-top: 18px;
  color: #536051;
}

.form-alert {
  margin-bottom: 20px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #fff4e8;
  color: #8d4a09;
}

.form-alert.success {
  background: #e9f6ea;
  color: #245c2b;
}

.error {
  margin-top: 6px;
  color: #c43d30;
  font-size: 13px;
}

.error-border {
  border-color: #c43d30;
}

@media (max-width: 639px) {
  .register-user-card {
    padding: 22px 18px;
    border-radius: 16px;
  }

  .field-label {
    flex-direction: column;
    gap: 2px;
  }
}
</style>
